import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Table, TableColumnsType } from 'antd';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { initEcharts, formatEChartsNumber } from 'lib/echarts';
import { METRIC_META, NIL_TOOLTIP } from './constants';
import { formatCompactDate, formatMetricValue, toNumber } from './utils';
import SafeImage from 'components/SafeImage';
import { Item } from 'views/Dashboard/ReviewAnalysis/ItemProblemTable';

type MetricCode = Dashboard.TikTokUS.MetricCode;

export interface SpsMetricDetailProps {
  metricCode: MetricCode;
  diagnosis?: Dashboard.TikTokUS.ShopMetricDiagnosis;
  topItems?: Dashboard.TikTokUS.TopItemsResp;
  /** 该指标数据不足（NIL）→ 内部区块整体不渲染 */
  nil?: boolean;
}

/** 区块标题：设计稿是「蓝色竖条 + 标题」；AI 区块用星形图标 */
function SectionTitle({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <h4
      className="flex items-center"
      style={{ gap: 8, fontSize: 14, fontWeight: 600, marginBottom: 12 }}
    >
      {icon ?? (
        <span
          style={{
            flex: '0 0 auto',
            width: 3,
            height: 14,
            borderRadius: 2,
            background: 'var(--ant-primary-color)',
          }}
        />
      )}
      {children}
    </h4>
  );
}

/** AI 分析的「双星」图标（一颗大星 + 右上小星），设计稿用的就是这个 */
const SparkleIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="var(--fett-ai-analysis-icon)"
    style={{ flex: '0 0 auto' }}
  >
    <path d="M9 3.2l1.85 5.15 5.15 1.85-5.15 1.85L9 17.2 7.15 12.05 2 10.2l5.15-1.85z" />
    <path d="M18 2.2l.95 2.65 2.65.95-2.65.95L18 9.4l-.95-2.65L14.4 5.8l2.65-.95z" />
  </svg>
);

/**
 * 计算口径 + 评估阈值：设计稿里是**同一行左右并排的浅灰底圆角块**，中间一条竖分割线。
 *
 * ⚠️ 评估阈值的方向：OTDR 是「越高越好」，比较方向要整体翻转
 *   越低越好 —— 优秀 `≤ excellent`，差 `≥ poor`
 *   越高越好 —— 优秀 `≥ excellent`，差 `≤ poor`
 */
function MetaBand({
  code,
  rule,
  benchmarks,
  value,
  valueUnit,
}: {
  code: MetricCode;
  rule?: Dashboard.TikTokUS.CalculationRule;
  benchmarks?: Dashboard.TikTokUS.Benchmarks;
  value?: string;
  valueUnit?: Dashboard.TikTokUS.ValueUnit;
}) {
  const { t } = useTranslate();

  if (!rule && !benchmarks) {
    return null;
  }

  /** 计数类数值补千分位（设计稿形式：`1,996（说明） / 271,353（说明） = 0.74%`） */
  const count = (raw?: string) => {
    const num = toNumber(raw ?? null);
    return num === null ? raw || '-' : formatEChartsNumber(num, '0,0');
  };

  /** 阈值与指标 value 同量纲，补上与指标一致的单位（设计稿里带 `%`） */
  const withUnit = (raw?: string) => formatMetricValue(raw, METRIC_META[code].unit);

  const lowerBetter = METRIC_META[code].better === 'lower';
  const excellent = benchmarks?.excellent_threshold;
  const poor = benchmarks?.poor_threshold;
  const [midStart, midEnd] = lowerBetter ? [excellent, poor] : [poor, excellent];

  const thresholds = benchmarks
    ? lowerBetter
      ? [
          { type: 'excellent', label: t('优秀'), text: `≤ ${withUnit(excellent)}` },
          { type: 'mid', label: '', text: `${withUnit(midStart)} - ${withUnit(midEnd)}` },
          { type: 'poor', label: t('sbs.差'), text: `≥ ${withUnit(poor)}` },
        ]
      : [
          { type: 'excellent', label: t('优秀'), text: `≥ ${withUnit(excellent)}` },
          { type: 'mid', label: '', text: `${withUnit(midStart)} - ${withUnit(midEnd)}` },
          { type: 'poor', label: t('sbs.差'), text: `≤ ${withUnit(poor)}` },
        ]
    : [];

  const small: React.CSSProperties = { fontSize: 12, color: 'var(--fett-text-tertiary)' };
  const num: React.CSSProperties = { fontSize: 15, fontWeight: 600 };

  return (
    <MetaBandBox>
      <div className="half">
        <h4>{t('计算口径')}</h4>
        {rule ? (
          <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap' }}>
            <b style={num}>{count(rule.numerator_value)}</b>
            <span style={small}>({rule.numerator_label})</span>
            <span style={small}>/</span>
            <b style={num}>{count(rule.denominator_value)}</b>
            <span style={small}>({rule.denominator_label})</span>
            {value ? (
              <>
                <span style={{ ...small, marginLeft: 12 }}>=</span>
                <b style={num}>{formatMetricValue(value, valueUnit)}</b>
              </>
            ) : null}
          </div>
        ) : (
          <span style={small}>-</span>
        )}
      </div>
      <div className="half">
        <h4>{t('评估阈值')}</h4>
        {thresholds.length ? (
          <div className="flex items-center" style={{ gap: 20, flexWrap: 'wrap' }}>
            {thresholds.map(({ type, label, text }) => (
              <span key={type} className="flex items-center" style={{ gap: 6 }}>
                <Dot type={type} />
                <span style={{ color: 'var(--fett-text-secondary)' }}>
                  {label ? `${label} ${text}` : text}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <span style={small}>-</span>
        )}
      </div>
    </MetaBandBox>
  );
}

/** ============ 近 30 天趋势（接口固定 30 天，不可切换） ============ */
function TrendSection({
  metricCode,
  diagnosis,
}: {
  metricCode: MetricCode;
  /** ⚠️ 传整个 diagnosis（不是 trend）—— 要用它自带的 metricCode 判断「这份数据属不属于当前指标」 */
  diagnosis?: Dashboard.TikTokUS.ShopMetricDiagnosis;
}) {
  const { t } = useTranslate();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<ReturnType<typeof initEcharts> | null>(null);
  const unit = METRIC_META[metricCode].unit;

  /** ① 图表实例**只初始化一次**（随组件挂载），切指标时不再反复 init / dispose */
  useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    const chart = initEcharts(chartRef.current);
    chartInstance.current = chart;
    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
      chartInstance.current = null;
    };
  }, []);

  /**
   * ② 数据变化时才 setOption。
   *
   * ⚠️ **只在数据属于当前指标时更新**：切指标时 useRequest 会先保留上一次的 data
   * （refreshDeps 变化不会立刻清空），所以只依赖 `trend` 的话会出现
   * 「点卡片 → 先用上一个指标的旧数据画一次 → 新数据到了再画一次」= 渲染两次，而且会闪。
   * 用 `diagnosis.metricCode` 与当前 `metricCode` 比对、丢掉过期那一帧，就只渲染一次。
   */
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !diagnosis || diagnosis.metricCode !== metricCode) {
      return;
    }

    const points = diagnosis.trend?.data_points;
    if (!points?.length) {
      chart.clear();
      return;
    }

    const dateList = points.map(({ record_date }) => formatCompactDate(record_date));

    /**
     * ⚠️ `initEcharts` 注册的主题把 valueAxis 的刻度写死成整数格式化（numeral `'0,0'`），
     * 而 SPS 的指标值都小于 1（如 0.41%），刻度会被全部渲染成 0 —— 必须在这里自己覆盖格式化。
     * 同理 tooltip 也要覆盖（主题里 tooltip.valueFormatter 也是整数）。
     */
    const formatValue = (value: unknown) => {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) {
        return '-';
      }
      return unit === 'HOURS'
        ? `${formatEChartsNumber(n, '0.0')}h`
        : `${formatEChartsNumber(n, '0.00')}%`;
    };

    chart.setOption({
      tooltip: { trigger: 'axis', valueFormatter: formatValue },
      grid: { left: '2%', right: '2%', top: '10%', bottom: '2%', containLabel: true },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: { formatter: (value: string) => value.slice(5) },
      },
      yAxis: { type: 'value', scale: true, axisLabel: { formatter: formatValue } },
      series: [
        {
          name: t(METRIC_META[metricCode].nameZh),
          type: 'line',
          data: points.map(({ value }) => toNumber(value)),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: { color: 'rgb(91, 143, 249)' },
          areaStyle: { color: 'rgba(91, 143, 249, .3)' },
        },
      ],
    });
  }, [diagnosis, metricCode, t, unit]);

  return (
    <Section>
      <SectionTitle>{t('近30天表现趋势')}</SectionTitle>
      <div ref={chartRef} style={{ height: 220 }} />
    </Section>
  );
}

const ProductCell = ({
  imageUrl,
  name,
  productId,
}: {
  imageUrl?: string;
  name?: string;
  productId?: string;
}) => (
  <Item>
    <SafeImage width={56} height={56} src={imageUrl} />
    <section>
      <p>{name}</p>
      {productId && <div>{`ID: ${productId}`}</div>}
    </section>
  </Item>
);

/**
 * ============ Top 商品 / 物流商 / 售后类型聚合 ============
 * 只有与当前 metricCode 对应的那个数组有值（NRR/NBFR/OTDR/AHT 四套不同结构）。
 */
function TopItemsSection({
  code,
  topItems,
  poorThreshold,
}: {
  code: MetricCode;
  topItems?: Dashboard.TikTokUS.TopItemsResp;
  /** 该指标的「差」阈值（来自 diagnosis.benchmarks），用于给前端算出的负评率标色 */
  poorThreshold?: string;
}) {
  const { t } = useTranslate();

  const { columns, dataSource, title } = useMemo<{
    columns: TableColumnsType<any>;
    dataSource: any[];
    title?: string;
  }>(() => {
    if (!topItems) {
      return { columns: [], dataSource: [] };
    }

    /** 排名：设计稿里是蓝色数字 */
    const rankColumn: TableColumnsType<any>[number] = {
      key: 'rank',
      title: t('排名'),
      width: 64,
      render: (_: unknown, __: unknown, index: number) => (
        <span
          style={index < 3 ? { color: 'var(--ant-primary-color)', fontWeight: 600 } : undefined}
        >
          {index + 1}
        </span>
      ),
    };

    if (code === 'NRR') {
      return {
        title: t('Top商品(差评最多)'),
        dataSource: topItems.nrr_top_product_items ?? [],
        columns: [
          rankColumn,
          {
            key: 'product',
            title: t('商品名称'),
            width: 320,
            render: (_, row: Dashboard.TikTokUS.TopProductItem) => (
              <ProductCell
                imageUrl={row.image_url}
                name={row.product_name}
                productId={row.product_id}
              />
            ),
          },
          {
            key: 'negative',
            dataIndex: 'negative_order_count',
            title: t('负评订单数'),
            width: 110,
          },
          {
            key: 'delivered',
            dataIndex: 'delivered_order_count',
            title: t('总交付订单数'),
            width: 120,
          },
          {
            key: 'negativeRate',
            title: t('负评率'),
            width: 100,
            /**
             * ⚠️ 接口不返回该列，前端算：差评订单数 / 送达订单数。
             * 阈值取同一指标 diagnosis 的 benchmarks.poor_threshold（与「评估阈值」同源），
             * 超过即标橙（设计稿用的橙色，不是红）；拿不到阈值就不标色。
             */
            render: (_, row: Dashboard.TikTokUS.TopProductItem) => {
              const delivered = row.delivered_order_count ?? 0;
              const negative = row.negative_order_count ?? 0;
              if (!delivered) {
                return '-';
              }
              const rate = (negative / delivered) * 100;
              const poor = toNumber(poorThreshold ?? null);
              const danger = poor !== null && rate > poor;
              return (
                <span
                  style={{
                    color: danger ? 'var(--ant-warning-color)' : undefined,
                    fontWeight: danger ? 600 : undefined,
                  }}
                >
                  {`${formatEChartsNumber(rate, '0.00')}%`}
                </span>
              );
            },
          },
          {
            key: 'reason',
            dataIndex: 'top_review_reasons',
            title: t('主要差评原因'),
            width: 260,
            // ⚠️ 官方只回最主要的一条，是**单个字符串**，不要按数组遍历
            render: (value?: string) => value || '-',
          },
        ],
      };
    }

    if (code === 'NBFR') {
      return {
        title: t('Top商品(退货最多)'),
        dataSource: topItems.nbfr_top_product_items ?? [],
        columns: [
          rankColumn,
          {
            key: 'product',
            title: t('商品名称'),
            width: 320,
            render: (_, row: Dashboard.TikTokUS.TopProductItem) => (
              <ProductCell
                imageUrl={row.image_url}
                name={row.product_name}
                productId={row.product_id}
              />
            ),
          },
          { key: 'return', dataIndex: 'return_order_count', title: t('退货单数'), width: 110 },
          {
            key: 'topReason',
            dataIndex: 'top_reason_order_count',
            title: t('主要原因订单数'),
            width: 140,
          },
          {
            key: 'reason',
            dataIndex: 'top_return_reasons',
            title: t('主要退货原因'),
            width: 260,
            render: (value?: string) => value || '-',
          },
        ],
      };
    }

    if (code === 'OTDR') {
      return {
        title: t('物流商聚合'),
        dataSource: topItems.otdr_logistics_items ?? [],
        columns: [
          rankColumn,
          {
            key: 'provider',
            dataIndex: 'logistics_provider',
            title: t('物流商'),
            width: 240,
            // 官方 OTDR 明细不返回商品信息，这里是物流商维度，没有商品图
          },
          {
            key: 'delivered',
            dataIndex: 'delivered_order_count',
            title: t('送达订单数'),
            width: 120,
          },
          {
            key: 'onTime',
            dataIndex: 'on_time_deliver_order_count',
            title: t('按时送达订单数'),
            width: 150,
          },
          {
            key: 'rate',
            dataIndex: 'on_time_deliver_rate',
            title: t('按时送达率'),
            width: 120,
            // ⚠️ 官方给的是**字符串小数**（如 "0.947"），不是百分数
            render: (value?: string) =>
              value ? `${formatEChartsNumber(Number(value) * 100, '0.00')}%` : '-',
          },
        ],
      };
    }

    if (code === 'AHT') {
      return {
        title: t('售后类型聚合'),
        dataSource: topItems.aht_aftersales_type_items ?? [],
        columns: [
          rankColumn,
          {
            key: 'product',
            title: t('商品名称'),
            width: 320,
            render: (_, row: Dashboard.TikTokUS.AftersalesTypeItem) => (
              <ProductCell
                imageUrl={row.image_url}
                name={row.product_name}
                productId={row.product_id}
              />
            ),
          },
          {
            key: 'duration',
            dataIndex: 'handle_duration_hours',
            title: t('售后处理时长(小时)'),
            width: 160,
            // ⚠️ 官方给的是**字符串**
            render: (value?: string) => formatMetricValue(value, 'HOURS'),
          },
          { key: 'return', dataIndex: 'return_order_count', title: t('退货单数'), width: 110 },
          {
            key: 'type',
            dataIndex: 'top_aftersales_type_name',
            title: t('主要售后类型'),
            width: 150,
          },
          { key: 'count', dataIndex: 'top_type_order_count', title: t('该类型订单数'), width: 130 },
        ],
      };
    }

    return { columns: [], dataSource: [] };
  }, [code, poorThreshold, topItems, t]);

  if (!title || !dataSource.length) {
    return null;
  }

  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <Table
        size="small"
        rowKey={(row) =>
          String(row.product_id ?? row.logistics_provider ?? '') + String(row.product_name ?? '')
        }
        columns={columns}
        dataSource={dataSource}
        pagination={false}
      />
    </Section>
  );
}

/**
 * ============ AI 分析 ============
 * 为空则不渲染整个区块（不显示「暂无分析」占位）。设计稿里是浅紫底圆角块 + 星形图标，
 * 且文案里的关键数字会高亮（接口给整句字符串，这里用保守的规则匹配来高亮）。
 */
const HIGHLIGHT_SPLIT = /(\d+(?:\.\d+)?%|\d+个[^，。；、！？\s]{1,8})/g;
const HIGHLIGHT_TEST = /^(\d+(?:\.\d+)?%|\d+个[^，。；、！？\s]{1,8})$/;

export function AiSection({ analyses }: { analyses?: Dashboard.TikTokUS.Analyses }) {
  const { t } = useTranslate();
  /** ⚠️ 实际接口给的是**数组**；元素可能是纯字符串或 `{details[], summaries[]}` —— 统一摊平成行 */
  const lines = (analyses ?? [])
    .flatMap((item) =>
      typeof item === 'string' ? [item] : [...(item?.details ?? []), ...(item?.summaries ?? [])],
    )
    .filter(Boolean);

  if (!lines.length) {
    return null;
  }

  const renderText = (text: string) =>
    text.split(HIGHLIGHT_SPLIT).map((part, index) =>
      HIGHLIGHT_TEST.test(part) ? (
        <b key={index} style={{ color: 'var(--fett-ai-analysis-highlight)' }}>
          {part}
        </b>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>{part}</span>
      ),
    );

  return (
    <AiBlock>
      {/* 设计稿：图标在左，与「标题 + 正文」整块**垂直居中**（不是标题行内联） */}
      <div className="flex" style={{ gap: 16, alignItems: 'center' }}>
        <SparkleIcon />
        <div className="flex flex-col" style={{ gap: 4, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fett-text-primary)' }}>
            {t('AI诊断分析')}
          </div>
          <div style={{ color: 'var(--fett-text-secondary)' }}>
            {lines.map((text) => (
              <div key={text}>{renderText(text)}</div>
            ))}
          </div>
        </div>
      </div>
    </AiBlock>
  );
}

function SpsMetricDetail({ metricCode, diagnosis, topItems, nil }: SpsMetricDetailProps) {
  const { t } = useTranslate();

  if (nil) {
    return (
      <Section>
        <span style={{ color: 'var(--fett-text-tertiary)' }}>{t(NIL_TOOLTIP)}</span>
      </Section>
    );
  }

  return (
    <Container>
      <MetaBand
        code={metricCode}
        rule={diagnosis?.calculationRule}
        benchmarks={diagnosis?.benchmarks}
        value={diagnosis?.value}
        valueUnit={diagnosis?.valueUnit}
      />
      <TrendSection metricCode={metricCode} diagnosis={diagnosis} />
      <TopItemsSection
        code={metricCode}
        topItems={topItems}
        poorThreshold={diagnosis?.benchmarks?.poor_threshold}
      />
      {/* <AiSection analyses={diagnosis?.analyses} /> */}
      {!diagnosis && (
        <Section>
          <span className="text-gray-400">{t('trade.加载中')}</span>
        </Section>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: var(--fett-bg-panel);
`;

const Section = styled.section`
  margin-top: 24px;
  /*
   * 表格按声明的列宽分配（table-layout: fixed）。否则超长商品名会把数字列挤到只剩几十像素，
   * 表头中文被逐字竖排（已踩过）。同时表头强制不换行。
   */
  .ant-table table {
    table-layout: fixed;
  }
  .ant-table th {
    white-space: nowrap;
  }
`;

/** 计算口径 | 评估阈值：浅灰底圆角块（左右留白），内部一条竖分割线 */
const MetaBandBox = styled.section`
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  background: var(--fett-bg-subtle);
  > .half {
    flex: 1 0 0;
    min-width: 0;
    padding: 12px 16px;
    &:last-of-type {
      border-left: 1px solid var(--fett-border-default);
    }
  }
`;

/** AI 分析块：浅蓝紫底圆角块（设计稿 #EBEFFF 一类的浅底），与上面区块保持 24px 节奏 */
const AiBlock = styled.section`
  margin-top: 24px;
  padding: 16px 20px;
  border-radius: 8px;
  background: var(--fett-ai-analysis-bg);
`;

const Dot = styled.span<{ type: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ type }) =>
    type === 'excellent'
      ? 'var(--ant-success-color)'
      : type === 'poor'
      ? 'var(--ant-error-color)'
      : 'var(--ant-warning-color)'};
`;

export default SpsMetricDetail;
